import React from "react";
import { Link } from "react-router-dom";
import { Sun, ArrowRight, Check, Zap, FileText, MapPinned, TrendingUp, PenLine, ChevronDown, Star } from "lucide-react";
import heroLandscape from "@/assets/hero-landscape.jpg";
import heroRoof from "@/assets/hero-roof.jpg";
import card1 from "@/assets/card-1.jpg";
import card2 from "@/assets/card-2.jpg";
import card3 from "@/assets/card-3.jpg";
import satellite from "@/assets/satellite.jpg";

function Ring({ pct = 0.72, center, dark, size = 60 }) {
  const R = 26, C = 2 * Math.PI * R;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r={R} fill="none" stroke={dark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.10)"} strokeWidth="5" />
        <circle cx="32" cy="32" r={R} fill="none" stroke="#E0A82E" strokeWidth="5" strokeLinecap="round" strokeDasharray={`${C * pct} ${C}`} />
      </svg>
      {center && (
        <div className={`absolute inset-0 flex items-center justify-center text-sm font-semibold ${dark ? "text-white" : "text-zinc-900"}`}>{center}</div>
      )}
    </div>
  );
}

const Eyebrow = ({ children }) => (
  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">{children}</div>
);

const PLANS = [
  { name: "Starter", price: 49, features: ["1–3 utenti", "Catalogo base", "20 simulazioni / mese", "Supporto via email"] },
  { name: "Pro", price: 149, featured: true, features: ["Utenti illimitati", "Simulazioni illimitate", "Branding sul PDF", "Assistente AI", "Pagina pubblica preventivi"] },
  { name: "Business", price: 399, features: ["Multi-sede", "Listini multipli", "API & White label", "Supporto prioritario"] },
];

const FAQS = [
  { q: "Serve installare qualcosa?", a: "No. SolarFlow funziona dal browser, anche su tablet in cantiere: nessun software da installare." },
  { q: "I dati di produzione sono affidabili?", a: "Usiamo PVGIS, la banca dati della Commissione Europea, incrociata con l'area reale che disegni sul tetto." },
  { q: "Posso provarlo gratis?", a: "Sì. Crei il tuo workspace in due minuti, senza carta di credito, e fai subito la prima simulazione." },
  { q: "Il cliente può firmare online?", a: "Sì: firma il preventivo dal telefono e tu ricevi la notifica quando lo apre e quando lo accetta." },
  { q: "I dati miei e dei clienti sono al sicuro?", a: "Ogni azienda ha un workspace isolato, con ruoli e permessi. I dati sono cifrati e ospitati nell'Unione Europea." },
];

// NOTE: testimonianze PLACEHOLDER — sostituire con clienti reali prima della pubblicazione.
const TESTIMONIALS = [
  { quote: "Chiudo il preventivo mentre sono ancora sul tetto. Prima ci mettevo due giorni.", name: "Marco R.", role: "Installatore · Brescia" },
  { quote: "Il cliente vede subito risparmio e payback: la trattativa si sblocca da sola.", name: "Giulia F.", role: "Consulente energetico · Padova" },
  { quote: "Catalogo, margini e PDF in un posto solo. Basta fogli Excel la sera.", name: "Antonio L.", role: "Titolare · Bari" },
  { quote: "La simulazione satellitare fa colpo: il cliente capisce subito cosa avrà sul tetto.", name: "Sara M.", role: "Sales · Verona" },
  { quote: "La firma online ha tagliato i tempi morti: niente più giri per raccogliere documenti.", name: "Davide P.", role: "Installatore · Torino" },
  { quote: "Gestisco tre commerciali dalla pipeline. Vedo a colpo d'occhio dove siamo fermi.", name: "Elena C.", role: "Responsabile vendite · Roma" },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="rounded-2xl bg-white border border-zinc-200/70 overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
        <span className="font-medium">{q}</span>
        <ChevronDown size={18} className={`text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-5 -mt-1 text-sm text-zinc-500 leading-relaxed">{a}</div>}
    </div>
  );
}

export default function Landing() {
  return (
    <div className="bg-[#f4f3f0] text-zinc-900">
      {/* ===== HERO ===== */}
      <div className="px-3 sm:px-4 pt-3 sm:pt-4">
        <section className="relative rounded-[28px] overflow-hidden min-h-[86vh] flex flex-col">
          <img src={heroLandscape} alt="Campo fotovoltaico tra le colline" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/35" />

          {/* Nav */}
          <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-5">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-zinc-900"><Sun size={18} strokeWidth={2.5} /></div>
              <span className="font-semibold text-lg tracking-tight text-white">SolarFlow</span>
            </Link>
            <nav className="hidden md:flex items-center gap-1 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-2 py-1.5 text-sm text-white/90">
              <a href="#funzioni" className="px-3.5 py-1.5 rounded-full hover:bg-white/15 transition-colors">Funzioni</a>
              <a href="#prezzi" className="px-3.5 py-1.5 rounded-full hover:bg-white/15 transition-colors">Prezzi</a>
              <a href="#faq" className="px-3.5 py-1.5 rounded-full hover:bg-white/15 transition-colors">FAQ</a>
              <Link to="/login" className="px-3.5 py-1.5 rounded-full hover:bg-white/15 transition-colors">Accedi</Link>
            </nav>
            <Link to="/register" className="bg-white text-zinc-900 text-sm font-semibold rounded-full px-4 py-2.5 hover:bg-white/90 transition-colors" data-testid="landing-register">
              Inizia gratis
            </Link>
          </header>

          {/* Hero copy */}
          <div className="relative z-10 mt-auto p-6 sm:p-10">
            <div className="flex items-end justify-between gap-8 flex-wrap">
              <div className="max-w-2xl">
                <h1 className="text-white text-4xl sm:text-5xl xl:text-6xl font-semibold tracking-tight leading-[1.04]">
                  Il futuro del fotovoltaico,<br />in una sola visita.
                </h1>
                <p className="mt-5 text-white/80 text-base sm:text-lg max-w-md leading-relaxed">
                  Disegni il tetto sulla mappa, calcoli produzione e ROI reali, fai firmare il preventivo. Tutto davanti al cliente.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/login" className="bg-zinc-900 text-white font-semibold rounded-full px-6 py-3.5 flex items-center gap-2 hover:bg-zinc-800 transition-colors">
                    Prova la demo <ArrowRight size={16} />
                  </Link>
                  <Link to="/register" className="bg-white/10 backdrop-blur-md border border-white/25 text-white font-medium rounded-full px-6 py-3.5 hover:bg-white/20 transition-colors">
                    Crea workspace
                  </Link>
                </div>
              </div>

              {/* Right column: stat on top, simple feature pills below */}
              <div className="flex flex-col items-start sm:items-end gap-5">
                <div className="text-left sm:text-right text-white">
                  <div className="text-3xl font-semibold tabular-nums">4 sec</div>
                  <div className="text-xs text-white/70 mt-1">per il layout pannelli</div>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-2">
                  {[
                    { i: MapPinned, t: "Disegni il tetto" },
                    { i: TrendingUp, t: "Vedi il risparmio" },
                    { i: PenLine, t: "Firma subito" },
                  ].map((p) => {
                    const Ic = p.i;
                    return (
                      <span key={p.t} className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur-md px-4 py-2 text-sm text-white">
                        <Ic size={15} className="text-[#E8B84B]" />{p.t}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ===== SECTION: bento ===== */}
      <section id="funzioni" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Eyebrow>Più energia, più risparmio</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Tutto il ciclo di vendita, in un unico posto.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {/* Dark image card with gauge */}
          <div className="relative rounded-3xl overflow-hidden min-h-[320px] flex flex-col justify-between p-6 text-white">
            <img src={satellite} alt="Vista satellitare di un quartiere" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/40" />
            <div className="relative font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">Simulatore satellitare</div>
            <div className="relative">
              <div className="flex items-center gap-3">
                <Ring pct={0.65} center="" dark size={54} />
                <div className="text-3xl font-semibold leading-none">9,6<span className="text-base font-normal text-white/60 ml-1">kWp</span></div>
              </div>
              <div className="mt-3 text-sm text-white/85 max-w-[14rem]">Disegna il tetto e ottieni il layout pannelli in 4 secondi.</div>
            </div>
          </div>
          {/* Two light feature cards */}
          {[
            { i: Zap, t: "Produzione e ROI reali", d: "Dati PVGIS per i kWh annui. Risparmio, payback e CO₂ calcolati in tempo reale, davanti al cliente." },
            { i: FileText, t: "Preventivi che si firmano", d: "PDF brandizzato con firma online dal telefono. Vedi quando il cliente apre e accetta." },
          ].map((x, i) => {
            const Ic = x.i;
            return (
              <div key={i} className="rounded-3xl bg-white border border-zinc-200/70 p-7 flex flex-col">
                <div className="w-11 h-11 rounded-xl bg-zinc-900 text-white flex items-center justify-center mb-5"><Ic size={19} /></div>
                <h3 className="text-lg font-semibold">{x.t}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed mt-auto pt-6">{x.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ===== SECTION: savings split ===== */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div>
          <Eyebrow>Semplice da usare</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]">Preventivi facili, fatti con il solare.</h2>
          <p className="mt-4 text-zinc-500 leading-relaxed max-w-md">Niente più fogli Excel e PDF montati a sera: dal sopralluogo alla firma in un unico flusso.</p>
          <ul className="mt-6 space-y-3">
            {["Catalogo, listini e margini per ruolo", "Pacchetti Base / Smart / Premium pronti", "Pipeline kanban dal lead all'installato"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-zinc-700">
                <span className="w-5 h-5 rounded-full bg-zinc-900 text-white flex items-center justify-center shrink-0 mt-0.5"><Check size={12} /></span>
                {t}
              </li>
            ))}
          </ul>
          <Link to="/register" className="mt-8 inline-flex items-center gap-2 bg-zinc-900 text-white font-semibold rounded-full px-6 py-3.5 hover:bg-zinc-800 transition-colors">
            Inizia gratis <ArrowRight size={16} />
          </Link>
        </div>
        <div className="relative rounded-3xl overflow-hidden min-h-[380px]">
          <img src={heroRoof} alt="Impianto su tetto" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3.5 shadow-xl">
            <Ring pct={0.7} center={<Zap size={18} className="text-[#E0A82E]" fill="#E0A82E" />} size={54} />
            <div>
              <div className="text-xl font-semibold leading-none text-zinc-900">700<span className="text-xs font-normal text-zinc-400 ml-1">kWh/mese</span></div>
              <div className="text-xs text-zinc-500 mt-1.5 max-w-[8rem] leading-snug">Energia prodotta stimata</div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION: 3-up photo cards ===== */}
      <section id="come" className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Eyebrow>Come funziona</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Dal tetto al contratto, senza attriti.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { img: card1, n: "01", t: "Disegna il tetto", d: "Indirizzo, falda, esclusioni sulla mappa satellitare." },
            { img: card2, n: "02", t: "Genera la proposta", d: "Pannelli, produzione e ROI calcolati in automatico." },
            { img: card3, n: "03", t: "Chiudi il cliente", d: "Invia il preventivo e raccogli la firma online." },
          ].map((c) => (
            <div key={c.n} className="relative rounded-3xl overflow-hidden min-h-[440px] flex flex-col justify-end p-7 text-white">
              <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
              <div className="relative">
                <div className="font-mono text-xs text-white/70 mb-1">{c.n}</div>
                <h3 className="text-xl font-semibold mb-1">{c.t}</h3>
                <p className="text-sm text-white/80 leading-relaxed">{c.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="prezzi" className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Eyebrow>Prezzi semplici</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Un piano per ogni studio.</h2>
          <p className="mt-4 text-zinc-500">Prova gratis, senza carta di credito. Cambi o disdici quando vuoi.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4 items-start">
          {PLANS.map((p) => (
            <div key={p.name} className={`rounded-3xl p-7 border ${p.featured ? "bg-zinc-900 text-white border-zinc-900 shadow-2xl shadow-black/20" : "bg-white text-zinc-900 border-zinc-200/70"}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="font-semibold text-lg">{p.name}</span>
                {p.featured && <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#E0A82E] text-zinc-900 rounded-full px-2.5 py-1">Più scelto</span>}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-semibold tracking-tight">€{p.price}</span>
                <span className={p.featured ? "text-white/60 text-sm" : "text-zinc-400 text-sm"}>/mese</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-white/85" : "text-zinc-600"}`}>
                    <Check size={16} className={`shrink-0 mt-0.5 ${p.featured ? "text-[#E0A82E]" : "text-zinc-900"}`} />{f}
                  </li>
                ))}
              </ul>
              <Link to="/register" className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition-colors ${p.featured ? "bg-white text-zinc-900 hover:bg-white/90" : "bg-zinc-900 text-white hover:bg-zinc-800"}`}>
                Inizia gratis <ArrowRight size={15} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ===== FEEDBACK (marquee) ===== */}
      <section className="pb-16 sm:pb-24 overflow-hidden">
        <div className="text-center max-w-2xl mx-auto mb-12 px-4">
          <Eyebrow>Chi lo usa</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Pensato con chi vende impianti ogni giorno.</h2>
        </div>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 z-10 bg-gradient-to-r from-[#f4f3f0] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 z-10 bg-gradient-to-l from-[#f4f3f0] to-transparent" />
          <div className="marquee-track flex gap-4 w-max">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <div key={i} className="w-[340px] shrink-0 rounded-3xl bg-white border border-zinc-200/70 p-7 flex flex-col">
                <div className="flex gap-0.5 mb-4 text-[#E0A82E]">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={15} fill="#E0A82E" strokeWidth={0} />)}
                </div>
                <p className="text-[15px] leading-relaxed text-zinc-700">“{t.quote}”</p>
                <div className="mt-6 pt-5 border-t border-zinc-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-zinc-900 text-white flex items-center justify-center text-sm font-semibold">{t.name.slice(0, 1)}</div>
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                    <div className="text-xs text-zinc-400">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="text-center mb-10">
          <Eyebrow>Domande frequenti</Eyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Tutto chiaro prima di iniziare.</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <div className="px-3 sm:px-4 pb-4">
        <section className="relative rounded-[28px] overflow-hidden">
          <img src={heroLandscape} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 max-w-3xl mx-auto px-6 py-24 sm:py-28 text-center">
            <h2 className="text-white text-3xl sm:text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.08]">Il prossimo preventivo lo chiudi sul tetto, non in ufficio.</h2>
            <p className="mt-4 text-white/80 max-w-lg mx-auto">Crea il tuo workspace gratuito e fai la prima simulazione oggi. Nessuna carta richiesta.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/register" className="bg-white text-zinc-900 font-semibold rounded-full px-7 py-3.5 flex items-center gap-2 hover:bg-white/90 transition-colors">
                Inizia gratis <ArrowRight size={16} />
              </Link>
              <Link to="/login" className="bg-white/10 backdrop-blur-md border border-white/25 text-white font-medium rounded-full px-7 py-3.5 hover:bg-white/20 transition-colors">
                Prova la demo
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* ===== FOOTER ===== */}
      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 pb-8 border-b border-zinc-200">
          <div className="lg:col-span-2 max-w-xs">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-lg bg-zinc-900 flex items-center justify-center text-white"><Sun size={16} strokeWidth={2.5} /></div>
              <span className="font-bold tracking-tight text-lg">SolarFlow <span className="text-zinc-400 font-normal">Pro</span></span>
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed">Il gestionale visuale per chi vende e installa impianti fotovoltaici in Italia.</p>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3">Prodotto</div>
            <ul className="space-y-2 text-sm text-zinc-500">
              <li><a href="#funzioni" className="hover:text-zinc-900 transition-colors">Funzioni</a></li>
              <li><a href="#come" className="hover:text-zinc-900 transition-colors">Come funziona</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3">Inizia</div>
            <ul className="space-y-2 text-sm text-zinc-500">
              <li><Link to="/register" className="hover:text-zinc-900 transition-colors">Crea workspace</Link></li>
              <li><Link to="/login" className="hover:text-zinc-900 transition-colors">Accedi</Link></li>
            </ul>
          </div>
        </div>
        <div className="pt-5 text-xs text-zinc-400 flex flex-col sm:flex-row justify-between gap-2">
          <span>© 2026 SolarFlow Pro · Software gestionale per il fotovoltaico</span>
          <span>Fatto in Italia 🇮🇹</span>
        </div>
      </footer>
    </div>
  );
}
